import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { SITE } from "../config/site";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/hajj-marketing", label: "Hajj Marketing" },
  { to: "/umrah-marketing", label: "Umrah Marketing" },
  { to: "/resources", label: "Resources" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Logo = ({ light = false }) => {
  return (
    <Link to={"/"} data-testid="nav-logo" className="flex items-center gap-3.5 shrink-0">
      <img
        src="/assets/nexus-logo-transparent.png"
        alt="Nexus Catalyst Agency"
        className="h-12 w-auto"
      />
      <span className="leading-none">
        <span
          className={`block text-[15px] font-bold tracking-tight ${
            light ? "text-cream" : "text-charcoal"
          }`}
        >
          Nexus Catalyst
        </span>
        <span className="block text-[9px] font-semibold tracking-[0.35em] uppercase text-tan mt-1">
          Agency
        </span>
      </span>
    </Link>
  );
};

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/90 backdrop-blur-xl border-b border-charcoal/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            : "bg-cream/70 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 h-[86px] flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden 2xl:flex items-center gap-7" data-testid="nav-menu">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${slug(item.label)}`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive
                      ? "text-tan"
                      : "text-charcoal/70 hover:text-charcoal"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to={"/contact"}
              data-testid="nav-book-call-button"
              onClick={() => trackEvent("cta_click", { cta: "book_strategy_call", placement: "nav" })}
              className="group hidden md:inline-flex items-center gap-2 whitespace-nowrap bg-tan text-cream text-[13px] font-semibold tracking-wide px-6 py-3 transition-colors duration-300 hover:bg-charcoal"
            >
              {"Book a Strategy Call"}
              <ArrowUpRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <button
              data-testid="nav-hamburger-button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="2xl:hidden flex h-11 w-11 items-center justify-center border border-charcoal/15 text-charcoal hover:border-gold transition-colors"
            >
              <Menu size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-charcoal flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 geo-pattern-light opacity-[0.04]" />
            <div className="relative flex items-center justify-between px-6 h-[86px]">
              <Logo light />
              <button
                data-testid="nav-close-button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-cream/20 text-cream hover:border-gold transition-colors"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="relative flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-link-${slug(item.label)}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 py-2.5 border-b border-cream/10 ${
                        isActive ? "text-gold" : "text-cream"
                      }`
                    }
                  >
                    <span className="text-[11px] font-semibold tracking-[0.25em] text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-2xl md:text-3xl font-bold tracking-tight transition-transform duration-300 group-hover:translate-x-2">
                      {item.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="relative px-6 pb-8 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to={"/contact"}
                data-testid="mobile-nav-book-call-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-tan text-cream text-sm font-semibold px-6 py-4 hover:bg-gold transition-colors"
              >
                {"Book a Strategy Call"}
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </Link>
              <a
                href={SITE.blueprintUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-audit-button"
                className="flex-1 inline-flex items-center justify-center border border-cream/30 text-cream text-sm font-semibold px-6 py-4 hover:border-gold hover:text-gold transition-colors"
              >
                {"Free Customer Growth Blueprint"}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
